export type ChronopostServiceKey =
  | 'relais_13'
  | 'shop2shop'
  | 'relais_europe'
  | 'relais_dom'
  | 'home_13'
  | 'home_10'
  | 'home_18';

export type ChronopostServiceInfo = {
  label: string;
  amount_cents: number;
  amount_eur: number;
  product_code: string;
  requires_relay: boolean;
};

export type DeliveryPricingResponse = {
  colissimo: { amount_cents: number; amount_eur: number; logo_url?: string | null; enabled?: boolean };
  chronopost: {
    enabled: boolean;
    logo_url?: string | null;
    services: Partial<Record<ChronopostServiceKey, ChronopostServiceInfo>>;
  };
  mondial_relay: {
    enabled: boolean;
    logo_url?: string | null;
    services: {
      relay: { label: string; amount_cents: number; amount_eur: number };
    };
  };
  carrier_logos?: {
    colissimo: string | null;
    chronopost: string | null;
    mondial_relay: string | null;
  };
  disabled_modes?: DeliveryMode[];
};

export const CHRONOPOST_DELIVERY_MODES: DeliveryMode[] = [
  'chronopost_relais_13',
  'chronopost_shop2shop',
  'chronopost_relais_europe',
  'chronopost_relais_dom',
  'chronopost_home_13',
  'chronopost_home_10',
  'chronopost_home_18',
];

const CHRONOPOST_SERVICE_BY_MODE: Record<string, ChronopostServiceKey> = {
  chronopost_relais_13: 'relais_13',
  chronopost_shop2shop: 'shop2shop',
  chronopost_relais_europe: 'relais_europe',
  chronopost_relais_dom: 'relais_dom',
  chronopost_home_13: 'home_13',
  chronopost_home_10: 'home_10',
  chronopost_home_18: 'home_18',
};

export function normalizeDeliveryMode(mode: string | null | undefined): DeliveryMode | null {
  if (!mode) return null;
  if (mode === 'chronopost_home_49') return 'chronopost_relais_europe';
  if (mode === 'chronopost_home_4p') return 'chronopost_relais_dom';
  return mode as DeliveryMode;
}

export function isDeliveryModeDisabled(
  mode: DeliveryMode,
  pricing: DeliveryPricingResponse | null | undefined
): boolean {
  return (pricing?.disabled_modes ?? []).includes(mode);
}

function filterEnabledDeliveryModes(
  modes: DeliveryMode[],
  pricing: DeliveryPricingResponse | null | undefined
): DeliveryMode[] {
  return modes.filter((mode) => !isDeliveryModeDisabled(mode, pricing));
}

export type DeliveryMode =
  | 'colissimo_home'
  | 'colissimo_pickup'
  | 'chronopost_relais_13'
  | 'chronopost_shop2shop'
  | 'chronopost_relais_europe'
  | 'chronopost_relais_dom'
  | 'chronopost_home_13'
  | 'chronopost_home_10'
  | 'chronopost_home_18'
  | 'mondial_relay_pickup';

export type DeliveryCarrier = 'colissimo' | 'chronopost' | 'mondial_relay';

/** Ordre d'affichage des transporteurs (wizard dépôt, choix livraison). */
export const DELIVERY_CARRIER_DISPLAY_ORDER: DeliveryCarrier[] = [
  'mondial_relay',
  'colissimo',
  'chronopost',
];

export function deliveryModeToCarrier(mode: DeliveryMode): DeliveryCarrier {
  if (mode === 'colissimo_home' || mode === 'colissimo_pickup') return 'colissimo';
  if (CHRONOPOST_DELIVERY_MODES.includes(mode)) return 'chronopost';
  return 'mondial_relay';
}

export function isDeliveryCarrier(value: string): value is DeliveryCarrier {
  return value === 'colissimo' || value === 'chronopost' || value === 'mondial_relay';
}

export function chronopostServiceKeyForMode(mode: DeliveryMode): ChronopostServiceKey {
  return CHRONOPOST_SERVICE_BY_MODE[mode] ?? 'relais_13';
}

export function formatAmountEur(amount: number): string {
  const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/\.00$/, '');
  return `${formatted}€`;
}

export function formatDeliveryFlatLabel(): string {
  return '18€';
}

export function getDeliveryPriceLabel(
  mode: DeliveryMode | null,
  pricing: DeliveryPricingResponse | null | undefined
): string {
  if (!mode || !pricing) return formatDeliveryFlatLabel();
  if (mode === 'colissimo_home' || mode === 'colissimo_pickup') {
    return formatAmountEur(pricing.colissimo.amount_eur);
  }
  const serviceKey = chronopostServiceKeyForMode(mode);
  const chronopostService = pricing.chronopost?.services?.[serviceKey];
  if (chronopostService) {
    return formatAmountEur(chronopostService.amount_eur);
  }
  if (mode === 'mondial_relay_pickup') {
    return formatAmountEur(pricing.mondial_relay.services.relay.amount_eur);
  }
  return formatDeliveryFlatLabel();
}

export function getAvailableDeliveryModes(
  pricing: DeliveryPricingResponse | null | undefined
): DeliveryMode[] {
  const modes: DeliveryMode[] = [];
  if (pricing?.colissimo?.enabled !== false) {
    modes.push('colissimo_home', 'colissimo_pickup');
  }
  if (pricing?.chronopost?.enabled) {
    modes.push(...CHRONOPOST_DELIVERY_MODES);
  }
  if (pricing?.mondial_relay?.enabled) {
    modes.push('mondial_relay_pickup');
  }
  return filterEnabledDeliveryModes(modes, pricing);
}

export function getAvailableDeliveryCarriers(
  pricing: DeliveryPricingResponse | null | undefined
): DeliveryCarrier[] {
  const carriers = new Set<DeliveryCarrier>();
  for (const mode of getAvailableDeliveryModes(pricing)) {
    carriers.add(deliveryModeToCarrier(mode));
  }
  return DELIVERY_CARRIER_DISPLAY_ORDER.filter((carrier) => carriers.has(carrier));
}

export function getDeliveryCarrierLabel(carrier: DeliveryCarrier): string {
  switch (carrier) {
    case 'colissimo':
      return 'Colissimo';
    case 'chronopost':
      return 'Chronopost';
    case 'mondial_relay':
      return 'Mondial Relay';
    default:
      return 'Livraison';
  }
}

export function getDeliveryCarrierShortDescription(carrier: DeliveryCarrier): string {
  switch (carrier) {
    case 'colissimo':
      return 'La Poste — domicile ou point relais';
    case 'chronopost':
      return 'Relais ou livraison à domicile Chronopost';
    case 'mondial_relay':
      return 'Points Relais® et Lockers Mondial Relay';
    default:
      return '';
  }
}

export function getDeliveryCarrierPriceHint(
  carrier: DeliveryCarrier,
  pricing: DeliveryPricingResponse | null | undefined
): string {
  const modes = getAvailableDeliveryModes(pricing).filter(
    (m) => deliveryModeToCarrier(m) === carrier
  );
  if (modes.length === 0) return formatDeliveryFlatLabel();
  const amounts = modes.map((m) => getDeliveryPriceLabel(m, pricing));
  const unique = Array.from(new Set(amounts));
  return unique.length === 1 ? unique[0] : `à partir de ${unique[0]}`;
}

/** Montant admin (centimes) suggéré pour un transporteur — base pour un override de proposition. */
export function getDeliveryCarrierDefaultAmountCents(
  carrier: DeliveryCarrier | null | undefined,
  pricing: DeliveryPricingResponse | null | undefined
): number {
  if (!carrier || !pricing) return 1800;
  if (carrier === 'colissimo') return pricing.colissimo.amount_cents || 1800;
  if (carrier === 'mondial_relay') {
    return pricing.mondial_relay?.services?.relay?.amount_cents || 1500;
  }
  if (carrier === 'chronopost') {
    const services = Object.values(pricing.chronopost?.services ?? {});
    const first = services.find((s) => typeof s?.amount_cents === 'number');
    return first?.amount_cents || 1800;
  }
  return 1800;
}

export function getDeliveryModeLabel(
  mode: DeliveryMode,
  pricing?: DeliveryPricingResponse | null
): string {
  switch (mode) {
    case 'colissimo_home':
      return 'Colissimo domicile';
    case 'colissimo_pickup':
      return 'Colissimo point relais';
    case 'mondial_relay_pickup':
      return pricing?.mondial_relay?.services?.relay?.label ?? 'Mondial Relay';
    default: {
      const serviceKey = chronopostServiceKeyForMode(mode);
      return pricing?.chronopost?.services?.[serviceKey]?.label ?? mode;
    }
  }
}

export function getDeliveryModeShortDescription(mode: DeliveryMode): string {
  switch (mode) {
    case 'colissimo_home':
      return 'Livraison à l’adresse du propriétaire';
    case 'colissimo_pickup':
      return 'Retrait en bureau de poste ou chez un commerçant partenaire';
    case 'chronopost_relais_13':
      return 'Livraison express en relais — dès le lendemain avant 13 h';
    case 'chronopost_shop2shop':
      return 'Livraison relais à relais — sous 2 à 4 jours, tarif réduit';
    case 'chronopost_relais_europe':
      return 'Retrait en relais Pickup en Europe (2 à 6 jours)';
    case 'chronopost_relais_dom':
      return 'Retrait en relais Outre-mer (4 à 5 jours)';
    case 'chronopost_home_13':
      return 'Livraison à domicile — lendemain avant 13 h';
    case 'chronopost_home_10':
      return 'Livraison à domicile — lendemain 8 h–10 h';
    case 'chronopost_home_18':
      return 'Livraison à domicile — lendemain avant 18 h';
    case 'mondial_relay_pickup':
      return 'Retrait en Point Relais® Mondial Relay';
    default:
      return '';
  }
}
